import React, { useRef, useEffect, useState, useContext } from 'react';
import Header from '../components/Header';
import FilterSection from '../components/FilterSection';
import ItemTable from '../components/ItemTable';
import { saveInventory, findAllInventoryItems, uploadInventoryExcel, filterInventoryItems, deleteInventoryItem } from '../../actions/inventoryActions';
import { Box, Button, Container, Grid, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, InputAdornment, Typography, Snackbar, Alert, Divider } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';


import { findAllCategories } from '../../actions/uiActions';
import { CompanyContext } from '../../CompanyProvider';
import { LoadingContext } from '../../LoadingContext';

function InventoryPage({handleLogout, onReady}) {
  const fileInputRef = useRef();
  const companyCode = useContext(CompanyContext);
  const [items, setItems] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [categories, setCategories] = useState([{ categoryID: 'All', categoryName: 'All' }]);
  const [catLoading, setCatLoading] = useState(false);
  const { loading,setLoading, error,setError } = useContext(LoadingContext);

  const [newItem, setNewItem] = useState({
    sku: '',
    name: '',
    category: '',
    description: '',
    mrp: '',
    sellingPrice: '',
    stock: '',
    image: null,
    additionalImages: [],
    tax: '',
  });
  const [successMsg, setSuccessMsg] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  

  // Handles saving a new or edited inventory item
  const handleSaveNewItem = async () => {
    // Validate mandatory fields
    if (!newItem.sku || !newItem.sku.trim()) {
      setError('SKU is mandatory');
      return;
    }
    if (!newItem.name || !newItem.name.trim()) {
      setError('Item Name is mandatory');
      return;
    }
    if (!newItem.mrp || newItem.mrp === '') {
      setError('MRP is mandatory');
      return;
    }
    if (!newItem.sellingPrice || newItem.sellingPrice === '') {
      setError('Selling Price is mandatory');
      return;
    }
    if (!newItem.tax || newItem.tax === '') {
      setError('Tax is mandatory');
      return;
    }
    if (newItem.stock === '') {
      setError('Stock is mandatory');
      return;
    }
    if (!newItem.image && !editMode) {
      setError('Main Image is mandatory');
      return;
    }

    try {
      setLoading(true);
      const { image, additionalImages, ...inventoryData } = newItem;
      
      // Auto-populate description with item name if empty
      if (!inventoryData.description || !inventoryData.description.trim()) {
        inventoryData.description = inventoryData.name;
      }
      
      console.log("Saving inventory item:", inventoryData);
      // If editing, include a flag or id for update (assume backend supports update by SKU)
      await saveInventory(inventoryData, image, additionalImages, companyCode);
      const data = await findAllInventoryItems(companyCode);
      setItems(data);
      setOpenDialog(false);
      setSuccessMsg(editMode ? "Item updated successfully!" : "Item saved successfully!");
      setNewItem({
        sku: '',
        name: '',
        category: '',
        description: '',
        mrp: '',
        sellingPrice: '',
        stock: '',
        image: null,
        additionalImages: [],
        tax: '',
      });
      setEditMode(false);
      setEditingItem(null);
    } catch (e) {
      alert(editMode ? 'Failed to update item.' : 'Failed to create item.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    async function fetchItems() {
      setLoading(true);
      setError(null);
      try {
        const data = await findAllInventoryItems(companyCode);
        setItems(data);
      } catch (err) {
        setError('Failed to load inventory items');
      } finally {
        setLoading(false);
        if (onReady) onReady();
      }
    }
    fetchItems();
  }, []);

  useEffect(() => {
    async function loadCategories() {
      setLoading(true);
      setCatLoading(true);
      try {
        const data = await findAllCategories(companyCode);
        setCategories([{ categoryID: 'All', categoryName: 'All' }, ...data]);
      } catch (e) {
        setCategories([{ categoryID: 'All', categoryName: 'All' }]);
      } finally {
        setCatLoading(false);
        setLoading(false);
      }
    }
    loadCategories();
  }, []);

  const handleUploadClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };
  const handleFilterList = (filterObject) => {
    console.log("Filtering with:", filterObject);
    setLoading(true);
    filterInventoryItems(filterObject)
      .then((data) => {
        setItems(data);
      })
      .catch((err) => {
        setError('Failed to filter inventory items');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleExcelFileChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const fileName = file.name.toLowerCase();
      
      // Check if file extension is .xls or .xlsx
      if (!fileName.endsWith('.xls') && !fileName.endsWith('.xlsx')) {
        setError('Invalid file format. Please upload a .xls or .xlsx file.');
        // Reset file input
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        const result = await uploadInventoryExcel(file, companyCode);
        console.log("Excel upload result:", result);
        if (result.success && result.status === 200) {
          // Success - refresh the list
          const data = await findAllInventoryItems(companyCode);
          setItems(data);
          setSuccessMsg(result.message || 'Excel uploaded successfully!');
        } else if (!result.success && result.status === 400) {
          // Validation error from backend
          setError(result.message || 'Validation failed. Please check your Excel file.');
        } else {
          // Other errors
          setError(result.message || 'Failed to upload Excel file');
        }
      } catch (err) {
        setError('Failed to upload Excel file: ' + (err.message || ''));
      } finally {
        setLoading(false);
        // Reset file input so the same file can be uploaded again
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    }
  };

  const handleNewItem = () => {
    setEditMode(false);
    setEditingItem(null);
    setNewItem({
      sku: '',
      name: '',
      category: '',
      description: '',
      mrp: '',
      sellingPrice: '',
      stock: '',
      image: null,
      additionalImages: [],
      tax: '',
    });
    setOpenDialog(true);
  };

  // Handler for edit action from ItemTable
  const handleEditItem = (item) => {
    setEditMode(true);
    setEditingItem(item);
    setNewItem({
      ...item,
      image: null, // User must re-upload if changing image
      additionalImages: [], // User must re-upload if changing additional images
    });
    setOpenDialog(true);
  };

  // Handler for delete action from ItemTable
  const handleDeleteItem = (sku) => {
    setItemToDelete(sku);
    setDeleteDialogOpen(true);
  };

  // Confirm delete action
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    
    setDeleteDialogOpen(false);
    try {
      setLoading(true);
      const response = await deleteInventoryItem(itemToDelete, companyCode);
      
      // Check if response status is 200
      if (response) {
        // Refresh the inventory list
        const data = await findAllInventoryItems(companyCode);
        setItems(data);
        setSuccessMsg('Item deleted successfully!');
      } else {
        setError('Failed to delete item. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete item. Please try again.');
    } finally {
      setLoading(false);
      setItemToDelete(null);
    }
  };

  // Cancel delete action
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  // Download Excel template with inventory data
  const handleDownloadTemplate = () => {
    // Define headers
    const headers = [
      'SKU',
      'Company',
      'Item Name',
      'Detailed Description',
      'Primary Image',
      'Additional Image',
      'Stock Quantity',
      'Selling Price',
      'MRP',
      'Category',
      'Sub Category',
      'Sub Sub Category (if any)',
      'Colors (if any)',
      'Size (if any)',
      'Tax'
    ];

    // Helper function to safely join array or string
    const safeJoin = (value) => {
      if (Array.isArray(value)) {
        return value.join(';');
      } else if (typeof value === 'string') {
        return value;
      }
      return '';
    };

    // Create data rows
    let dataRows = [];
    
    // Add inventory items if available
    if (items && items.length > 0) {
      items.forEach(item => {
        const row = [
          item.sku || '',
          item.companyCode || '',
          item.name || '',
          item.description || '',
          item.image || '',
          safeJoin(item.additionalImages),
          item.stock || '',
          item.sellingPrice || '',
          item.mrp || '',
          item.category || '',
          item.subCategory || '',
          '', // Sub Sub Category - empty for new items
          safeJoin(item.supportedColors),
          safeJoin(item.supportedSizes),
          item.tax || ''
        ];
        dataRows.push(row);
      });
    } else {
      // Add one empty row if no items exist
      dataRows.push(new Array(headers.length).fill(''));
    }

    // Create HTML table for Excel
    let htmlContent = '<table border="1"><thead><tr>';
    headers.forEach(header => {
      htmlContent += `<th>${header}</th>`;
    });
    htmlContent += '</tr></thead><tbody>';
    
    dataRows.forEach(row => {
      htmlContent += '<tr>';
      row.forEach(cell => {
        htmlContent += `<td>${cell}</td>`;
      });
      htmlContent += '</tr>';
    });
    htmlContent += '</tbody></table>';

    // Create blob with Excel MIME type
    const element = document.createElement('a');
    const file = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
    element.href = URL.createObjectURL(file);
    element.download = 'inventory_template.xls';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    setSuccessMsg('Template downloaded successfully!');
  };

  return (
    <Box sx={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start'}}>
      <Header userName="Sandeep" handleLogout={handleLogout} />
      <Container
        sx={{
          pt: 4,
          pb: 4,
          mt:2,
          background: '#ffffff',
          border: '1px solid #e8edf5',
          borderRadius: 4,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 20px rgba(0,0,0,0.04)',
          minWidth: "95%",
          minHeight: '80vh',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a3c6e', mb: 1.5, mt: 0, letterSpacing: 0.5, fontSize: 22, textAlign: 'left' }}>
          Inventory Management
        </Typography>
        <Divider sx={{ mb: 2, borderColor: '#e2e8f0' }} />
        <FilterSection onList={handleFilterList} />
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={8}>
            <Box />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls"
              style={{ display: 'none' }}
              onChange={handleExcelFileChange}
            />
          <Button variant="outlined" color="primary" sx={{ minWidth: 140, borderColor: '#1a3c6e', color: '#1a3c6e', fontWeight: 700, borderRadius: 2, background: 'rgba(26,60,110,0.07)' }} onClick={handleNewItem}>
            + New Item
          </Button>
          <Button variant="outlined" color="primary" sx={{ minWidth: 160, borderColor: '#1a3c6e', color: '#1a3c6e', fontWeight: 700, borderRadius: 2, background: 'rgba(26,60,110,0.07)' }} onClick={handleDownloadTemplate} startIcon={<FileDownloadIcon />}>
            Download Template
          </Button>
          <Button variant="contained" color="primary" sx={{ minWidth: 140, background: '#1a3c6e', color: '#fff', fontWeight: 700, borderRadius: 2, boxShadow: '0 2px 8px 0 rgba(26,60,110,0.13)' }} onClick={handleUploadClick} startIcon={<CloudUploadIcon />}>
            Upload Excel
          </Button>
          </Grid>
        </Grid>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>Loading...</Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', color: 'red', py: 4 }}>{error}</Box>
        ) : (
          <ItemTable items={items} onEditItem={handleEditItem} onDeleteItem={handleDeleteItem} />
        )}

        {/* New Item Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              background: '#ffffff',
              boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
              border: 'none',
            }
          }}
        >
          <DialogTitle sx={{
            fontWeight: 700,
            color: '#0f172a',
            background: '#ffffff',
            borderBottom: '1px solid #e8edf5',
            fontSize: 18,
            letterSpacing: 0,
          }}>{editMode ? 'Edit Item' : 'Add New Item'}</DialogTitle>
          <DialogContent dividers sx={{
            background: '#ffffff',
            borderBottom: '1px solid #e8edf5',
            pb: 2
          }}>
            <Grid container spacing={1}>
              <Grid item xs={12} sx={{width: '30%',mb: 1}}>
                <TextField 
                  label="SKU" 
                  value={newItem.sku} 
                  onChange={e => setNewItem({ ...newItem, sku: e.target.value })} 
                  fullWidth 
                  size="large" 
                  required 
                  InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sx={{width: '65%',mb: 1}}>
                <TextField 
                  select 
                  label="Category" 
                  value={newItem.category} 
                  onChange={e => setNewItem({ ...newItem, category: e.target.value })} 
                  fullWidth 
                  size="large" 
                  required 
                  disabled={catLoading}
                  SelectProps={{ MenuProps: { PaperProps: { sx: { background: '#ffffff' } } } }}
                  InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat.categoryID} value={cat.categoryID} sx={{ color: '#1a3c6e', fontWeight: 500 }}>{cat.categoryName}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sx={{width: '98%',mb: 1}}>
                <TextField 
                  label="Item Name" 
                  value={newItem.name} 
                  onChange={e => setNewItem({ ...newItem, name: e.target.value })} 
                  fullWidth 
                  size="large" 
                  required 
                  InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sx={{width: '98%',mb: 1}}>
                <TextField 
                  label="Description" 
                  value={newItem.description} 
                  onChange={e => setNewItem({ ...newItem, description: e.target.value })} 
                  fullWidth 
                  size="large" 
                  multiline 
                  minRows={3} 
                  InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sx={{width: '25%',mb: 1}}>
                <TextField 
                  label="MRP" 
                  value={newItem.mrp} 
                  onChange={e => setNewItem({ ...newItem, mrp: e.target.value })} 
                  fullWidth 
                  size="large" 
                  type="number"
                  required
                  InputProps={{ 
                    startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon fontSize="small" /></InputAdornment>,
                    sx: { background: '#f8fafc', borderRadius: 2 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sx={{width: '25%',mb: 1}}>
                <TextField 
                  label="Selling Price" 
                  value={newItem.sellingPrice} 
                  onChange={e => setNewItem({ ...newItem, sellingPrice: e.target.value })} 
                  fullWidth 
                  size="large" 
                  type="number"
                  required
                  InputProps={{ 
                    startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon fontSize="small" /></InputAdornment>,
                    sx: { background: '#f8fafc', borderRadius: 2 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sx={{width: '20%',mb: 1}}>
                <TextField 
                  label="Tax (%)" 
                  value={newItem.tax} 
                  onChange={e => setNewItem({ ...newItem, tax: e.target.value })} 
                  fullWidth 
                  size="large" 
                  type="number"
                  required
                  InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sx={{width: '23%',mb: 1}}>
                <TextField 
                  label="Stock" 
                  value={newItem.stock} 
                  onChange={e => setNewItem({ ...newItem, stock: e.target.value })} 
                  fullWidth 
                  size="large" 
                  type="number"
                  required
                  InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sx={{width: '98%',mb: 1}}>
                <Box
                  sx={{
                    border: '2px dashed #cbd5e1',
                    borderRadius: 3,
                    background: '#f8fafc',
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: '#94a3b8', background: '#f0f4f8' }
                  }}
                  onClick={() => document.getElementById('main-image-upload').click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    e.preventDefault();
                    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                      setNewItem({ ...newItem, image: e.dataTransfer.files[0] });
                    }
                  }}
                >
                  <input
                    id="main-image-upload"
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={e => setNewItem({ ...newItem, image: e.target.files[0] })}
                  />
                  <AddPhotoAlternateIcon sx={{ fontSize: 48, color: '#1a3c6e', mb: 1 }} />
                  <Typography sx={{ color: '#1a3c6e', fontWeight: 400 }}>Main Image <span style={{ color: '#d32f2f' }}>*</span></Typography>
                  <Typography sx={{ fontWeight: 400, my: 1, color: '#1a3c6e' }}>Drag & Drop <b>OR</b></Typography>
                  <Button variant="contained" sx={{ background: '#1a3c6e', color: '#fff', borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }} onClick={e => { e.stopPropagation(); document.getElementById('main-image-upload').click(); }}>
                    Upload
                  </Button>
                  {newItem.image && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: 13, color: '#1a3c6e' }}>{newItem.image.name}</Typography>
                      <Box sx={{ mt: 1 }}>
                        <img
                          src={URL.createObjectURL(newItem.image)}
                          alt="preview"
                          style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8, border: '1.5px solid #1a3c6e' }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sx={{width: '98%',mb: 1}}>
                <Box
                  sx={{
                    border: '2px dashed #cbd5e1',
                    borderRadius: 3,
                    background: '#f8fafc',
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: '#94a3b8', background: '#f0f4f8' }
                  }}
                  onClick={() => document.getElementById('additional-image-upload').click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    e.preventDefault();
                    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                      setNewItem({ ...newItem, additionalImages: Array.from(e.dataTransfer.files) });
                    }
                  }}
                >
                  <input
                    id="additional-image-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={e => setNewItem({ ...newItem, additionalImages: Array.from(e.target.files) })}
                  />
                  <AddPhotoAlternateIcon sx={{ fontSize: 48, color: '#1a3c6e', mb: 1 }} />
                  <Typography sx={{ color: '#1a3c6e', fontWeight: 400 }}>Additional Images</Typography>
                  <Typography sx={{ fontWeight: 400, my: 1, color: '#1a3c6e' }}>Drag & Drop <b>OR</b></Typography>
                  <Button variant="contained" sx={{ background: '#1a3c6e', color: '#fff', borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }} onClick={e => { e.stopPropagation(); document.getElementById('additional-image-upload').click(); }}>
                    Upload
                  </Button>
                  {newItem.additionalImages && newItem.additionalImages.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                      {newItem.additionalImages.map((file, idx) => (
                        <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
                          <Typography sx={{ fontSize: 13, color: '#1a3c6e' }}>{file.name}</Typography>
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`preview-${idx}`}
                            style={{ maxWidth: 80, maxHeight: 80, borderRadius: 6, border: '1.5px solid #1a3c6e', marginTop: 4 }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{
            background: '#ffffff',
            borderTop: '1px solid #e8edf5',
            pb: 2,
            pt: 2
          }}>
            <Button onClick={() => { setOpenDialog(false); setEditMode(false); setEditingItem(null); }} color="secondary" sx={{
              color: '#1a3c6e',
              borderColor: '#1a3c6e',
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              background: 'rgba(26,60,110,0.07)'
            }}>Cancel</Button>
            <Button
              onClick={handleSaveNewItem}
              color="primary"
              variant="contained"
              sx={{
                background: '#1a3c6e',
                color: '#fff',
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
                textTransform: 'none',
                boxShadow: '0 2px 8px 0 rgba(26,60,110,0.13)'
              }}
            >
              {editMode ? 'Update' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={handleCancelDelete}
          PaperProps={{
            sx: {
              borderRadius: '16px',
              background: '#ffffff',
              boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
              border: 'none',
            }
          }}
        >
          <DialogTitle sx={{
            fontWeight: 700,
            color: '#0f172a',
            background: '#ffffff',
            borderBottom: '1px solid #e8edf5',
            fontSize: 18,
            letterSpacing: 0,
          }}>Confirm Delete</DialogTitle>
          <DialogContent sx={{
            background: '#ffffff',
            pt: 3,
            pb: 2
          }}>
            <Typography sx={{ color: '#1a3c6e', fontSize: 16 }}>
              Are you sure you want to delete item with SKU: <strong>{itemToDelete}</strong>?
            </Typography>
            <Typography sx={{ color: '#8e99af', fontSize: 14, mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{
            background: '#ffffff',
            borderTop: '1px solid #e8edf5',
            pb: 2,
            pt: 2
          }}>
            <Button 
              onClick={handleCancelDelete} 
              sx={{
                color: '#1a3c6e',
                borderColor: '#1a3c6e',
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
                textTransform: 'none',
                background: 'rgba(26,60,110,0.07)'
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              sx={{
                background: '#d32f2f',
                color: '#fff',
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
                textTransform: 'none',
                boxShadow: '0 2px 8px 0 rgba(211,47,47,0.13)',
                '&:hover': {
                  background: '#c62828'
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      <Snackbar open={!!successMsg} autoHideDuration={3000} onClose={() => setSuccessMsg("")} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSuccessMsg("")} severity="success" sx={{ width: '100%' }}>
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default InventoryPage;
